import { useState } from "react";

import { ssnValidator, useOrganization } from "@asd/customer-portal-ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Backdrop, Box, Button, Card, CircularProgress, Stack, TextField, Typography, Link as Weblink } from "@mui/material";
import { useMask } from "@react-input/mask";
import { Controller, useForm } from "react-hook-form";

import { useKeycloak } from "~/components/authorization";
import { UserService } from "~/services";
import { routeToErrorPage } from "~/utils/navigationMap";

const SsnForm = () => {
  const { onVerificationCallback } = useKeycloak();

  const { configuration } = useOrganization();

  const { control, register, handleSubmit, clearErrors, watch } = useForm({
    defaultValues: {
      ssn: "",
      confirmSsn: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const [showSsn, setShowSsn] = useState(true);
  const [showConfirmSsn, setShowConfirmSsn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const ssnInputRef = useMask({
    mask: "___-__-____",
    replacement: { _: /\d/ },
    modify: ({ value, data, selectionStart, selectionEnd }) => {
      const newValue = value.slice(0, selectionStart) + (data ?? "") + value.slice(selectionEnd);
      return { showMask: !!newValue?.length && /\d/.test(newValue) };
    },
  });

  const confirmSsnInputRef = useMask({
    mask: "___-__-____",
    replacement: { _: /\d/ },
    modify: ({ value, data, selectionStart, selectionEnd }) => {
      const newValue = value.slice(0, selectionStart) + (data ?? "") + value.slice(selectionEnd);
      return { showMask: !!newValue?.length && /\d/.test(newValue) };
    },
  });

  return (
    <Stack alignSelf="center" direction="column" flexGrow={1} justifyContent="center" maxWidth="m" padding={2}>
      <Card elevation={6} sx={{ padding: 4, width: "100%", minWidth: (theme) => theme.breakpoints.values.sm / 2 }}>
        <Typography align="center" color="custom.main" fontWeight="bold" mb={4} variant="h4" gutterBottom>
          Complete Registration
        </Typography>
        <Typography align="center" mb={2} gutterBottom>
          Enter the Social Security Number of the Participant/Member to complete Registration
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit(async ({ ssn }) => {
            setIsLoading(true);
            const linkResponse = await UserService.linkUserToParticipant({ ssn });
            setIsLoading(false);

            if (linkResponse?.status === 204) {
              onVerificationCallback!();
            } else {
              routeToErrorPage();
            }
          })}
          position="relative"
          noValidate
        >
          <Controller
            control={control}
            defaultValue=""
            name="ssn"
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...register("ssn", ssnValidator)}
                  {...field}
                  autoComplete="off"
                  error={!!error}
                  helperText={error ? error.message : null}
                  inputMode="numeric"
                  inputRef={ssnInputRef}
                  label="Social Security Number"
                  onBlur={() => setShowSsn(false)}
                  onChange={(e) => {
                    field.onChange(e);
                    clearErrors("ssn");
                  }}
                  onFocus={() => setShowSsn(true)}
                  placeholder="123-45-6789"
                  sx={{ mb: 2 }}
                  type={showSsn ? "text" : "password"}
                  required
                />
              </>
            )}
          />
          <Controller
            control={control}
            defaultValue=""
            name="confirmSsn"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...register("confirmSsn", {
                  ...ssnValidator,
                  validate: (value) => {
                    if (watch("ssn") !== value) {
                      return "SSNs do not match";
                    }
                  },
                })}
                {...field}
                autoComplete="off"
                error={!!error}
                helperText={error ? error.message : null}
                inputMode="numeric"
                inputRef={confirmSsnInputRef}
                label="Confirm Social Security Number"
                onBlur={() => setShowConfirmSsn(false)}
                onChange={(e) => {
                  field.onChange(e);
                  clearErrors("confirmSsn");
                }}
                onFocus={() => setShowConfirmSsn(true)}
                placeholder="123-45-6789"
                type={showConfirmSsn ? "text" : "password"}
                required
              />
            )}
          />
          <Button endIcon={<ArrowForwardIcon />} sx={{ mt: 3, mb: 2 }} type="submit" fullWidth>
            Submit
          </Button>
          {!!isLoading && (
            <>
              <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, position: "absolute" }} open />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  justifySelf: "center",
                  alignContent: "center",
                  zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
              >
                <CircularProgress size={150} />
              </Box>
            </>
          )}
        </Box>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Weblink href={configuration.branding.homepageUrl} rel="noopener noreferrer" target="_blank">
            {configuration.branding.name}
            {configuration.branding.withTM && (
              <Typography component="sup" fontSize="xx-small" variant="caption">
                TM
              </Typography>
            )}{" "}
            Homepage
          </Weblink>
        </Box>
      </Card>
    </Stack>
  );
};

export default SsnForm;
